/**
 * Redraw the mini cart after a LimeSpot "Add" inside the drawer.
 * LimeSpot posts to /cart/add itself and does not tell the theme, so the
 * drawer stays stale. Watch its Add buttons, wait for the cart to change,
 * then call window.refreshCart() (assets/cart-refresh.js). Becomes a no-op
 * once LimeSpot calls refreshCart() directly. Do not add this to
 * snippets/limespot.liquid — the app overwrites that snippet (checksum).
 */
(() => {
  const ADD_SELECTOR = '.ls-add-to-cart';
  const POLL_INTERVAL_MS = 400;
  const POLL_ATTEMPTS = 10;

  let polling = false;

  const cartUrl = () => `${window.Theme?.routes?.cart_url || '/cart'}.js`;

  const readDrawerItemCount = (drawer) => {
    const count = Number.parseInt(drawer.querySelector('[ref="cartItemCount"]')?.textContent ?? '', 10);
    return Number.isNaN(count) ? null : count;
  };

  const fetchCartItemCount = async () => {
    // no-store: iOS Safari otherwise serves /cart.js from cache and the count never changes
    const response = await fetch(cartUrl(), { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    const cart = await response.json();
    return typeof cart.item_count === 'number' ? cart.item_count : null;
  };

  const waitForCartChange = async (baseline) => {
    for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      const count = await fetchCartItemCount();
      if (count !== null && count !== baseline) return true;
    }
    return false;
  };

  document.addEventListener(
    'click',
    async (event) => {
      if (polling || typeof window.refreshCart !== 'function') return;
      if (!(event.target instanceof Element)) return;

      const button = event.target.closest(ADD_SELECTOR);
      const drawer = button?.closest('cart-drawer-component');
      if (!button || !drawer) return;

      polling = true;
      try {
        const baseline = readDrawerItemCount(drawer) ?? (await fetchCartItemCount());
        if (await waitForCartChange(baseline)) await window.refreshCart();
      } catch (error) {
        console.error('LimeSpot cart refresh failed', error);
      } finally {
        polling = false;
      }
    },
    { capture: true }
  );
})();
